import { api } from './client';
import type { Epic, PaginatedResponse, StructuredDescription, Task } from './types';
import type { Job } from './mailbox';
import type { DeployRecordView, FixJobTarget } from './pull-requests';

/** ENG-421: an epic as GET /epics/:id returns it, with its newest deploy records (newest first). */
export type EpicDetail = Epic & { deployRecords?: DeployRecordView[] };

export type EpicStateFilter = 'active' | 'implemented' | 'archived';

export interface EpicFilters {
  teamId?: string | undefined;
  createdBy?: string | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  includeArchived?: boolean | undefined;
  state?: EpicStateFilter | undefined;
  orderBy?: 'createdAt' | 'updatedAt' | 'name' | undefined;
  orderDirection?: 'asc' | 'desc' | undefined;
  /** Add each epic's build facts (new look). */
  withFacts?: boolean | undefined;
}

export interface CreateEpicInput {
  name: string;
  description?: string | undefined;
  teamId: string;
  icon?: string | undefined;
  color?: string | undefined;
  epicRequestId?: string | undefined;
}

export interface UpdateEpicInput extends Partial<Omit<CreateEpicInput, 'teamId'>> {
  id: string;
}

export const epicsApi = {
  list: (filters: EpicFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, String(value));
    });
    const queryString = params.toString();
    return api.get<PaginatedResponse<Epic>>(`/epics${queryString ? `?${queryString}` : ''}`);
  },

  get: async (id: string, options: { withFacts?: boolean } = {}) => {
    const response = await api.get<{ data: EpicDetail & { structuredDesc?: string | null } }>(
      `/epics/${id}${options.withFacts ? '?withFacts=true' : ''}`
    );
    // Parse structuredDesc JSON string from API into object
    if (response.data.structuredDesc && typeof response.data.structuredDesc === 'string') {
      try {
        (response.data as Epic).structuredDesc = JSON.parse(response.data.structuredDesc) as StructuredDescription;
      } catch {
        (response.data as Epic).structuredDesc = null;
      }
    }
    return response as { data: EpicDetail };
  },

  create: (input: CreateEpicInput) => api.post<{ data: Epic }>('/epics', input),

  update: ({ id, ...input }: UpdateEpicInput) =>
    api.put<{ data: Epic }>(`/epics/${id}`, input),

  delete: (id: string) => api.delete<void>(`/epics/${id}`),

  archive: (id: string) => api.post<{ data: Epic }>(`/epics/${id}/archive`, {}),

  unarchive: (id: string) => api.post<{ data: Epic }>(`/epics/${id}/unarchive`, {}),

  markImplemented: (id: string) => api.post<{ data: Epic }>(`/epics/${id}/implement`, {}),

  /**
   * Transfer an epic between personal and team scope
   */
  transfer: (id: string, input: { direction: 'personal-to-team' | 'team-to-personal'; teamId?: string }) =>
    api.post<{ data: Epic }>(`/epics/${id}/transfer`, input),

  /**
   * Report what an implemented epic is missing (ENG-328): creates a follow-up
   * task under the chosen feature and queues a build.
   */
  reportGap: (id: string, input: ReportGapInput) =>
    api
      .post<{ data: ReportGapResult }>(`/epics/${encodeURIComponent(id)}/report-gap`, input)
      .then((r) => r.data),

  /** The owner decides a build that asked for a decision is done as it is. */
  acceptWrapUp: (id: string, note?: string) =>
    api
      .post<{ data: EpicWrapUpView }>(`/epics/${encodeURIComponent(id)}/wrap-up/accept`, note ? { note } : {})
      .then((r) => r.data),

  /** The epic's latest wrap-up and derived display state (ENG-323). Accepts a UUID or identifier. */
  getWrapUp: (id: string) =>
    api.get<{ data: EpicWrapUpView }>(`/epics/${encodeURIComponent(id)}/wrap-up`).then((r) => r.data),
};

/** The most a wrap-up finding can be when reporting a gap (mirrors the API's REPORT_GAP_FINDING_MAX). */
export const REPORT_GAP_FINDING_MAX = 2000;

/** Body for POST /epics/:id/report-gap (ENG-328). */
export interface ReportGapInput {
  featureId: string;
  description: string;
  title?: string;
  /** The wrap-up finding this follow-up comes from, when there is one. */
  finding?: string;
  target: FixJobTarget;
}

/** Where the follow-up build runs. */
export type ReportGapBuild =
  | { on: 'pull_request_branch'; branch: string; pullRequestUrl: string }
  | { on: 'new_branch'; previousPullRequestUrl: string | null };

export interface ReportGapResult {
  task: Task;
  job: Job;
  build: ReportGapBuild;
}

/** A wrap-up attempt's outcome (mirrors the API's wrapUpStatusValues). */
export type WrapUpStatus = 'running' | 'ready' | 'needs_decision' | 'failed';

/** What the epic shows for its build, in precedence order (ENG-323). */
export type EpicDisplayState = 'follow_up' | 'merged' | WrapUpStatus | 'none';

/** One project check the runner ran during wrap-up. */
export interface WrapUpCheck {
  name: string;
  passed: boolean;
  timedOut?: boolean;
  durationMs?: number;
  outputTail?: string;
  /** True when the check failed and the runner ran it again (ENG-396). */
  retried?: boolean;
  /**
   * Why a failed check failed (ENG-396): "caused" by this build, "base" —
   * already failing on the base branch, or "flaky" — passed when run again.
   * Absent on passing checks; a failure without one counts as caused.
   */
  classification?: WrapUpCheckClassification;
}

export type WrapUpCheckClassification = 'caused' | 'base' | 'flaky';

const CHECK_CLASSIFICATIONS: readonly string[] = ['caused', 'base', 'flaky'];

/** A Prisma migration the build added, classified by the API (ENG-325). */
export interface WrapUpMigration {
  path: string;
  safety: 'additive' | 'destructive';
  reasons: string[];
}

/** One wrap-up attempt. checks/findings/migrations are runner-written JSON. */
export interface WrapUp {
  id: string;
  epicId: string;
  attempt: number;
  /** "accepted": the owner decided the build is done as it is. */
  status: WrapUpStatus | 'accepted';
  checks: unknown[];
  findings: unknown[];
  migrations: unknown[];
  reviewComment: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /epics/:id/wrap-up */
export interface EpicWrapUpView {
  wrapUp: WrapUp | null;
  followUpOpen: boolean;
  displayState: EpicDisplayState;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** The well-formed checks on a wrap-up; anything malformed is dropped. */
export function wrapUpChecks(wrapUp: Pick<WrapUp, 'checks'> | null | undefined): WrapUpCheck[] {
  if (!wrapUp || !Array.isArray(wrapUp.checks)) return [];
  return wrapUp.checks.flatMap((c) =>
    isRecord(c) && typeof c.name === 'string' && typeof c.passed === 'boolean'
      ? [
          {
            name: c.name,
            passed: c.passed,
            timedOut: c.timedOut === true,
            ...(c.retried === true && { retried: true }),
            ...(!c.passed && {
              classification:
                typeof c.classification === 'string' && CHECK_CLASSIFICATIONS.includes(c.classification)
                  ? (c.classification as WrapUpCheckClassification)
                  : 'caused',
            }),
          },
        ]
      : [],
  );
}

/** The wrap-up's findings as text; non-string entries are dropped. */
export function wrapUpFindings(wrapUp: Pick<WrapUp, 'findings'> | null | undefined): string[] {
  if (!wrapUp || !Array.isArray(wrapUp.findings)) return [];
  return wrapUp.findings.filter((f): f is string => typeof f === 'string' && f.trim() !== '');
}

/** The migrations the API classified as destructive on this wrap-up. */
export function destructiveWrapUpMigrations(
  wrapUp: Pick<WrapUp, 'migrations'> | null | undefined,
): WrapUpMigration[] {
  if (!wrapUp || !Array.isArray(wrapUp.migrations)) return [];
  return wrapUp.migrations.flatMap((m) =>
    isRecord(m) && typeof m.path === 'string' && m.safety === 'destructive'
      ? [
          {
            path: m.path,
            safety: 'destructive' as const,
            reasons: Array.isArray(m.reasons) ? m.reasons.filter((r): r is string => typeof r === 'string') : [],
          },
        ]
      : [],
  );
}
